import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
  InputAdornment,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";

import {
  School as SchoolIcon,
  Close as CloseIcon,
  PersonOutline,
  CalendarMonth,
  Assessment,
  SaveOutlined,
  EditOutlined,
  LockOutlined,
  LockOpenOutlined,
  InfoOutlined,
} from "@mui/icons-material";

import api from "../../api/api";
import permissionaudio from "./audio/cantuploadpermission.mp3";

/* ============================================================
   COLORS
============================================================ */

const COLORS = {
  navy: "#062A4B",
  deepBlue: "#063A6B",
  gold: "#F6C944",
  orange: "#C07500",
  background: "#F3F6FB",
  white: "#FFFFFF",
  text: "#243447",
  muted: "#718096",
  border: "#E2E8F0",
  success: "#15803D",
  danger: "#DC2626",
};

/* ============================================================
   SCORE FIELDS
============================================================ */

const SCORE_FIELDS = [
  {
    key: "quizScore",
    field: "quiz_score",
    label: "Quiz Score",
    description: "Quiz assessment",
    required: false,
    defaultZero: true,
  },
  {
    key: "assignmentScore",
    field: "assignment_score",
    label: "Assignment Score",
    description: "Written assignments",
    required: false,
    defaultZero: true,
  },
  {
    key: "participationScore",
    field: "participation_score",
    label: "Participation Score",
    description: "Class participation",
    required: false,
    defaultZero: true,
  },
  {
    key: "testScore",
    field: "test_score",
    label: "Test Score",
    description: "Test assessment",
    required: true,
    defaultZero: false,
  },
  {
    key: "scaleGrade",
    field: "scale_grade",
    label: "Scale Grade",
    description: "Optional grading scale value",
    required: false,
    defaultZero: false,
  },
];

const EMPTY_SCORES = {
  quizScore: "0",
  assignmentScore: "0",
  participationScore: "0",
  testScore: "",
  scaleGrade: "",
};

/* ============================================================
   HELPERS
============================================================ */

const normalizeRole = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const getId = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "object") return value.id ?? "";
  return value;
};

const isEmptyScore = (value) =>
  value === "" ||
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

const getOptionLabel = (option) =>
  option?.name ||
  option?.full_name ||
  option?.student_name ||
  option?.subject_name ||
  option?.period_name ||
  option?.grade_class_name ||
  "";

const getBackendErrorMessage = (error) => {
  const data = error?.response?.data;

  if (!data) {
    if (error?.message === "Network Error" || !error?.response) {
      return (
        "Unable to connect to the server. Check your internet connection " +
        "or confirm that the backend server is running."
      );
    }

    return error?.message || "An unexpected error occurred.";
  }

  if (typeof data === "string") return data;
  if (typeof data.error === "string") return data.error;
  if (typeof data.detail === "string") return data.detail;
  if (typeof data.message === "string") return data.message;

  if (Array.isArray(data)) {
    return data.map(String).join("\n");
  }

  if (typeof data === "object") {
    return Object.entries(data)
      .map(([field, errors]) => {
        const readableField = field
          .replace(/_/g, " ")
          .replace(/\b\w/g, (character) => character.toUpperCase());

        const message = Array.isArray(errors)
          ? errors
              .map((item) =>
                typeof item === "object"
                  ? JSON.stringify(item)
                  : String(item)
              )
              .join(", ")
          : typeof errors === "object" && errors !== null
            ? JSON.stringify(errors)
            : String(errors);

        return `${readableField}: ${message}`;
      })
      .join("\n");
  }

  return "The server returned an unknown error.";
};

/* ============================================================
   COMPONENT
============================================================ */

const AddAndUpDateGrades = ({
  token,
  userType,

  gradeDistributions = [],
  gradeClasses = [],
  subjects = [],
  periods = [],
  selectedYear,

  // Parent component compatibility.
  page = 0,
  rowsPerPage = 25,
  searchTerm,
  search,

  fetchGradeDistributions,
  refreshGrades,
  onRefresh,

  setAlertMessage,
  setAlertSeverity,
  setSnackbarOpen,

  editGradeId,
  setEditGradeId,

  // When the parent conditionally renders this component,
  // the dialog opens automatically.
  open,

  onClose,
}) => {
  const role = normalizeRole(userType);

  const canManageGrades = ["admin", "principal"].includes(role);
  const canCreateGrades = ["admin", "principal", "teacher"].includes(role);
  const isTeacher = role === "teacher";

  const isControlled = typeof open === "boolean";

  const [dialogOpen, setDialogOpen] = useState(
    isControlled ? open : true
  );

  const [selectedGradeClass, setSelectedGradeClass] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("");

  const [filteredStudents, setFilteredStudents] = useState([]);
  const [scores, setScores] = useState({ ...EMPTY_SCORES });

  const [editingId, setEditingId] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const [loadingStudents, setLoadingStudents] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [studentsError, setStudentsError] = useState("");

  const selectedYearId = selectedYear?.id || "";

  const effectiveSearch =
    searchTerm !== undefined ? searchTerm : search;

  const selectedGrade = useMemo(
    () =>
      (gradeDistributions || []).find(
        (grade) => String(grade.id) === String(editingId)
      ),
    [gradeDistributions, editingId]
  );

  /* ============================================================
     FIELD STYLING
  ============================================================ */

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "11px",
      backgroundColor: COLORS.white,
      minHeight: 50,
      transition: "all 0.2s ease",
      "& fieldset": {
        borderColor: COLORS.border,
      },
      "&:hover fieldset": {
        borderColor: COLORS.deepBlue,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.gold,
        borderWidth: 2,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: COLORS.deepBlue,
    },
    "& .MuiFormHelperText-root": {
      marginLeft: "2px",
      fontSize: "10px",
    },
  };

  /* ============================================================
     SWEETALERT STYLING
  ============================================================ */

  useEffect(() => {
    const styleId = "grade-form-alert-styles";

    if (document.getElementById(styleId)) return undefined;

    const style = document.createElement("style");
    style.id = styleId;

    style.textContent = `
      .grade-form-alert {
        width: min(420px, calc(100% - 32px)) !important;
        padding: 28px 26px 24px !important;
        border: 1px solid #E2E8F0 !important;
        border-radius: 18px !important;
        font-family: "Segoe UI", Arial, sans-serif !important;
        box-shadow: 0 24px 70px rgba(6,42,75,.22) !important;
      }

      .grade-form-alert .swal2-icon {
        margin: 0 auto 18px !important;
      }

      .grade-form-alert-title {
        color: #062A4B !important;
        font-size: 22px !important;
        font-weight: 800 !important;
      }

      .grade-form-alert-message {
        color: #64748B !important;
        font-size: 14px !important;
        line-height: 1.7 !important;
        white-space: pre-line !important;
      }

      .grade-form-alert-button {
        min-width: 125px !important;
        padding: 12px 24px !important;
        border: none !important;
        border-radius: 9px !important;
        background: #062A4B !important;
        color: #FFFFFF !important;
        font-size: 13px !important;
        font-weight: 750 !important;
        cursor: pointer !important;
      }

      .grade-form-alert-button:hover {
        background: #063A6B !important;
      }

      .grade-form-alert-cancel-button {
        min-width: 100px !important;
        padding: 12px 20px !important;
        margin-left: 8px !important;
        border: 1px solid #E2E8F0 !important;
        border-radius: 9px !important;
        background: #FFFFFF !important;
        color: #062A4B !important;
        font-size: 13px !important;
        font-weight: 750 !important;
        cursor: pointer !important;
      }
    `;

    document.head.appendChild(style);

    return () => {
      style.remove();
    };
  }, []);

  /* ============================================================
     CONTROLLED / UNCONTROLLED DIALOG
  ============================================================ */

  useEffect(() => {
    if (isControlled) {
      setDialogOpen(open);
    }
  }, [open, isControlled]);

  const closeDialog = useCallback(() => {
    if (submitting) return;

    setDialogOpen(false);
    setEditGradeId?.(null);
    onClose?.();
  }, [submitting, setEditGradeId, onClose]);

  /* ============================================================
     ALERT HELPERS
  ============================================================ */

  const showFormAlert = useCallback(
    async ({
      icon = "info",
      title,
      text,
      confirmButtonText = "Understood",
      timer,
      showConfirmButton = true,
    }) => {
      setSnackbarOpen?.(false);
      setAlertMessage?.(text);
      setAlertSeverity?.(icon === "success" ? "success" : "error");

      return Swal.fire({
        icon,
        title,
        text,
        confirmButtonText,
        confirmButtonColor: COLORS.navy,
        timer,
        showConfirmButton,
        buttonsStyling: false,
        customClass: {
          popup: "grade-form-alert",
          title: "grade-form-alert-title",
          htmlContainer: "grade-form-alert-message",
          confirmButton: "grade-form-alert-button",
        },
        allowOutsideClick: !submitting,
        allowEscapeKey: !submitting,
      });
    },
    [
      setAlertMessage,
      setAlertSeverity,
      setSnackbarOpen,
      submitting,
    ]
  );

  const showBackendError = useCallback(
    (error, title = "Unable to Save Grade") => {
      console.error(
        "Grade Distribution Error:",
        error?.response?.data || error
      );

      return showFormAlert({
        icon: "error",
        title,
        text: getBackendErrorMessage(error),
      });
    },
    [showFormAlert]
  );

  const playPermissionAudio = useCallback(() => {
    try {
      const audio = new Audio(permissionaudio);
      audio.volume = 1;

      const playPromise = audio.play();

      if (playPromise?.catch) {
        playPromise.catch((error) => {
          console.warn("Permission audio could not play:", error);
        });
      }
    } catch (error) {
      console.warn("Unable to initialize permission audio:", error);
    }
  }, []);

  const showPermissionDenied = useCallback(
    async (message) => {
      playPermissionAudio();

      return showFormAlert({
        icon: "error",
        title: "Permission Denied",
        text: message,
      });
    },
    [playPermissionAudio, showFormAlert]
  );

  /* ============================================================
     LOAD STUDENTS FOR SELECTED CLASS
  ============================================================ */

  useEffect(() => {
    if (!selectedGradeClass || !selectedYearId) {
      setFilteredStudents([]);
      setLoadingStudents(false);
      setStudentsError("");
      return undefined;
    }

    let cancelled = false;

    const loadStudents = async () => {
      setLoadingStudents(true);
      setStudentsError("");

      try {
        const response = await api.get(
          `/students-in-class/${selectedGradeClass}/`,
          {
            headers: token
              ? { Authorization: `Bearer ${token}` }
              : undefined,
            params: {
              academic_year: selectedYearId,
            },
          }
        );

        if (cancelled) return;

        const responseData = response.data;

        setFilteredStudents(
          Array.isArray(responseData)
            ? responseData
            : responseData?.results || []
        );
      } catch (error) {
        if (cancelled) return;

        console.error(
          "Error loading students:",
          error?.response?.data || error
        );

        setFilteredStudents([]);
        setStudentsError(getBackendErrorMessage(error));
      } finally {
        if (!cancelled) {
          setLoadingStudents(false);
        }
      }
    };

    loadStudents();

    return () => {
      cancelled = true;
    };
  }, [selectedGradeClass, selectedYearId, token]);

  /* ============================================================
     RESET FORM
  ============================================================ */

  const resetForm = useCallback(() => {
    setSelectedGradeClass("");
    setSelectedStudent("");
    setSelectedSubject("");
    setSelectedPeriod("");

    setFilteredStudents([]);
    setStudentsError("");
    setScores({ ...EMPTY_SCORES });

    setEditingId(null);
    setEditMode(false);
  }, []);

  /* ============================================================
     FIND SELECTED OPTIONS
  ============================================================ */

  const findOption = useCallback((options, value) => {
    const targetId = getId(value);

    if (targetId === "") return null;

    return (
      (options || []).find(
        (option) => String(option.id) === String(targetId)
      ) || null
    );
  }, []);

  /* ============================================================
     OPEN FORM FOR EDITING
  ============================================================ */

  const handleOpenEdit = useCallback(
    (id) => {
      const item = (gradeDistributions || []).find(
        (grade) => String(grade.id) === String(id)
      );

      if (!item) {
        showFormAlert({
          icon: "error",
          title: "Grade Not Found",
          text:
            "The selected grade is not in the currently loaded records. " +
            "Refresh the grade list and try again.",
        });

        return;
      }

      if (!canManageGrades && !isTeacher) {
        showPermissionDenied(
          "Your role has view-only access. You cannot edit grade records."
        );
        return;
      }

      if (isTeacher && !item.allow_teacher_update) {
        showPermissionDenied(
          "This grade record is locked. Ask the administrator or principal " +
            "to grant you permission to edit this specific record."
        );
        return;
      }

      setEditingId(item.id);
      setEditMode(true);

      setSelectedGradeClass(getId(item.grade_class));
      setSelectedStudent(getId(item.student));
      setSelectedSubject(getId(item.subject));
      setSelectedPeriod(getId(item.period));

      setScores({
        quizScore: isEmptyScore(item.quiz_score)
          ? "0"
          : String(item.quiz_score),

        assignmentScore: isEmptyScore(item.assignment_score)
          ? "0"
          : String(item.assignment_score),

        participationScore: isEmptyScore(item.participation_score)
          ? "0"
          : String(item.participation_score),

        testScore: isEmptyScore(item.test_score)
          ? ""
          : String(item.test_score),

        scaleGrade: isEmptyScore(item.scale_grade)
          ? ""
          : String(item.scale_grade),
      });

      setDialogOpen(true);
    },
    [
      gradeDistributions,
      canManageGrades,
      isTeacher,
      showFormAlert,
      showPermissionDenied,
    ]
  );

  /* ============================================================
     OPEN FORM FROM PARENT editGradeId
  ============================================================ */

  useEffect(() => {
    if (editGradeId !== null && editGradeId !== undefined) {
      handleOpenEdit(editGradeId);
    }
  }, [editGradeId, handleOpenEdit]);

  /* ============================================================
     SCORE HANDLING / VALIDATION
  ============================================================ */

  const handleScoreChange = (field, value) => {
    setScores((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const validateScores = () => {
    for (const field of SCORE_FIELDS) {
      const value = scores[field.key];
      const empty = isEmptyScore(value);

      if (field.defaultZero && empty) continue;
      if (!field.required && empty) continue;

      if (empty) {
        return `${field.label} is required. Enter a score.`;
      }

      if (!Number.isFinite(Number(value))) {
        return `${field.label} must be a valid number.`;
      }

      if (Number(value) < 0) {
        return `${field.label} cannot be negative.`;
      }
    }

    return null;
  };

  /* ============================================================
     REFRESH PARENT DATA
  ============================================================ */

  const refreshParentGrades = useCallback(async () => {
    // Prefer the callback exposed by the updated parent.
    const refreshCallback = onRefresh || refreshGrades;

    if (typeof refreshCallback === "function") {
      await refreshCallback();
      return;
    }

    // Backwards compatibility with the previous component.
    if (typeof fetchGradeDistributions === "function") {
      await fetchGradeDistributions(
        page + 1,
        rowsPerPage,
        effectiveSearch
      );
    }
  }, [
    onRefresh,
    refreshGrades,
    fetchGradeDistributions,
    page,
    rowsPerPage,
    effectiveSearch,
  ]);

  /* ============================================================
     SUBMIT GRADE

     IMPORTANT:
     - Never send allow_teacher_update from this form.
     - Only Admin/Principal can grant permission using the
       dedicated backend action.
     - The backend consumes teacher permission after a
       successful teacher update.
  ============================================================ */

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting) return;

    if (!canCreateGrades) {
      await showPermissionDenied(
        "Your role does not have permission to create or update grades."
      );
      return;
    }

    if (
      !selectedGradeClass ||
      !selectedStudent ||
      !selectedSubject ||
      !selectedPeriod
    ) {
      await showFormAlert({
        icon: "warning",
        title: "Missing Required Fields",
        text: "Grade Class, Student, Subject, and Period are all required.",
      });
      return;
    }

    if (!selectedYearId) {
      await showFormAlert({
        icon: "warning",
        title: "Academic Year Required",
        text: "Select an academic year before saving this grade.",
      });
      return;
    }

    if (editMode && !editingId) {
      await showFormAlert({
        icon: "error",
        title: "Invalid Grade Record",
        text: "The grade record could not be identified. Close the form and try again.",
      });
      return;
    }

    if (isTeacher && editMode) {
      const currentGrade = selectedGrade;

      if (!currentGrade?.allow_teacher_update) {
        await showPermissionDenied(
          "This grade is locked. Ask the administrator or principal to unlock " +
            "this specific record before updating it."
        );
        return;
      }
    }

    const scoreError = validateScores();

    if (scoreError) {
      await showFormAlert({
        icon: "warning",
        title: "Check Assessment Scores",
        text: scoreError,
      });
      return;
    }

    const data = {
      academic_year: selectedYearId,
      grade_class: selectedGradeClass,
      student: selectedStudent,
      subject: selectedSubject,
      period: selectedPeriod,

      quiz_score: isEmptyScore(scores.quizScore)
        ? 0
        : Number(scores.quizScore),

      assignment_score: isEmptyScore(scores.assignmentScore)
        ? 0
        : Number(scores.assignmentScore),

      participation_score: isEmptyScore(scores.participationScore)
        ? 0
        : Number(scores.participationScore),

      test_score: Number(scores.testScore),

      scale_grade: isEmptyScore(scores.scaleGrade)
        ? null
        : Number(scores.scaleGrade),
    };

    // Deliberately do NOT include allow_teacher_update.
    // That permission is controlled by the backend grant action.

    try {
      setSubmitting(true);

      await api({
        method: editMode ? "patch" : "post",
        url: editMode
          ? `/api/users/grades-distribution/${editingId}/`
          : "/api/users/grades-distribution/",
        data,
        headers: token
          ? { Authorization: `Bearer ${token}` }
          : undefined,
        params: {
          academic_year: selectedYearId,
        },
      });

      setDialogOpen(false);
      setEditGradeId?.(null);

      await showFormAlert({
        icon: "success",
        title: "Saved Successfully",
        text: editMode
          ? isTeacher
            ? "Grade updated successfully. Your one-time editing permission has been used."
            : "Grade updated successfully."
          : "Grade created successfully.",
        timer: 2000,
        showConfirmButton: false,
      });

      await refreshParentGrades();

      onClose?.();
    } catch (error) {
      await showBackendError(error);
    } finally {
      setSubmitting(false);
    }
  };

  /* ============================================================
     DELETE HANDLER

     Kept for compatibility with callers, but no delete control
     is rendered in this form. Parent table owns deletion and
     only Admin/Principal should be allowed to delete.
  ============================================================ */

  const handleDeleteGrade = async () => {
    if (!canManageGrades) {
      await showPermissionDenied(
        "Only an administrator or principal can delete grade records."
      );
      return;
    }

    await showFormAlert({
      icon: "info",
      title: "Delete From Grade List",
      text: "Use the delete action in the grade distribution table.",
    });
  };

  /* ============================================================
     FORM GUARDS
  ============================================================ */

  if (!canCreateGrades && dialogOpen) {
    return (
      <Dialog open onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.navy, fontWeight: 800 }}>
          View-Only Access
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">
            Your role can view grade records but cannot create or modify them.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <>
      {/* Standalone use only. The updated parent can mount this
          component conditionally and let the dialog open directly. */}
      {!isControlled && !editGradeId && (
        <Button
          variant="contained"
          startIcon={<SchoolIcon />}
          disabled={!canCreateGrades || submitting}
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          sx={{
            minHeight: 44,
            px: 2.5,
            borderRadius: "10px",
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 800,
            bgcolor: COLORS.gold,
            color: COLORS.navy,
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#EFBF30",
            },
          }}
        >
          Add Grade
        </Button>
      )}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        aria-labelledby="grade-form-title"
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "620px",
            maxHeight: {
              xs: "100%",
              sm: "calc(100% - 48px)",
            },
            m: {
              xs: 0,
              sm: 2,
            },
            borderRadius: {
              xs: 0,
              sm: "18px",
            },
            overflow: "hidden",
            backgroundColor: COLORS.background,
            boxShadow: "0 25px 70px rgba(6,42,75,0.20)",
          },
        }}
      >
        {/* HEADER */}

        <DialogTitle
          id="grade-form-title"
          component="div"
          sx={{
            p: { xs: 2, sm: 2.5 },
            color: COLORS.white,
            background: `linear-gradient(120deg, ${COLORS.navy}, ${COLORS.deepBlue})`,
            borderBottom: `4px solid ${COLORS.gold}`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "13px",
                color: COLORS.navy,
                bgcolor: COLORS.gold,
              }}
            >
              {editMode ? (
                <EditOutlined sx={{ fontSize: 27 }} />
              ) : (
                <SchoolIcon sx={{ fontSize: 27 }} />
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: { xs: "18px", sm: "21px" },
                  lineHeight: 1.3,
                  fontWeight: 900,
                  letterSpacing: "-0.4px",
                }}
              >
                {editMode ? "Update Grade Record" : "Create Grade Record"}
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: "11px",
                  lineHeight: 1.6,
                  color: "rgba(255,255,255,0.76)",
                }}
              >
                {editMode
                  ? "Review and update the student's assessment details."
                  : "Enter the student's academic and assessment details."}
              </Typography>
            </Box>

            <Tooltip title="Close form">
              <span>
                <IconButton
                  onClick={closeDialog}
                  disabled={submitting}
                  aria-label="Close grade form"
                  sx={{
                    color: COLORS.white,
                    bgcolor: "rgba(255,255,255,0.10)",
                    borderRadius: "9px",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.18)",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Stack
            direction="row"
            flexWrap="wrap"
            gap={0.75}
            sx={{ mt: 2 }}
          >
            <Chip
              size="small"
              icon={
                <CalendarMonth
                  sx={{
                    color: `${COLORS.gold} !important`,
                    fontSize: "15px !important",
                  }}
                />
              }
              label={
                selectedYear?.name ||
                selectedYear?.year ||
                selectedYear?.academic_year ||
                "Academic Year"
              }
              sx={{
                maxWidth: "100%",
                height: 25,
                color: COLORS.white,
                bgcolor: "rgba(255,255,255,0.10)",
                fontSize: "10px",
                fontWeight: 750,
              }}
            />

            <Chip
              size="small"
              label={editMode ? "Editing" : "New Record"}
              sx={{
                height: 25,
                color: COLORS.navy,
                bgcolor: COLORS.gold,
                fontSize: "10px",
                fontWeight: 850,
              }}
            />

            {isTeacher && editMode && (
              <Chip
                size="small"
                icon={
                  <LockOpenOutlined
                    sx={{
                      color: "#FFFFFF !important",
                      fontSize: "15px !important",
                    }}
                  />
                }
                label="One-time edit"
                sx={{
                  height: 25,
                  color: COLORS.white,
                  bgcolor: COLORS.success,
                  fontSize: "10px",
                  fontWeight: 750,
                }}
              />
            )}
          </Stack>
        </DialogTitle>

        {/* FORM CONTENT */}

        <DialogContent
          sx={{
            p: { xs: 1.75, sm: 2.5 },
            pt: { xs: 2, sm: 2.5 },
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#CBD5E1",
              borderRadius: 10,
            },
          }}
        >
          <Box
            component="form"
            id="grade-distribution-form"
            onSubmit={handleSubmit}
            noValidate
          >
            {/* STUDENT INFORMATION */}

            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: "13px",
                border: `1px solid ${COLORS.border}`,
                bgcolor: COLORS.white,
                mb: 2,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Box
                  sx={{
                    width: 33,
                    height: 33,
                    borderRadius: "9px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#EFF6FF",
                    color: COLORS.deepBlue,
                  }}
                >
                  <PersonOutline sx={{ fontSize: 20 }} />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      color: COLORS.navy,
                      fontWeight: 850,
                      fontSize: "13px",
                    }}
                  >
                    Student Information
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.25,
                      color: COLORS.muted,
                      fontSize: "10px",
                    }}
                  >
                    Select the class and student receiving this grade.
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={1.75}>
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={gradeClasses || []}
                    value={findOption(gradeClasses, selectedGradeClass)}
                    disabled={submitting || (isTeacher && editMode)}
                    getOptionLabel={getOptionLabel}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    onChange={(_, value) => {
                      setSelectedGradeClass(value?.id ?? "");
                      setSelectedStudent("");
                      setFilteredStudents([]);
                      setStudentsError("");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Grade Class"
                        placeholder="Choose a grade class"
                        required
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={filteredStudents}
                    loading={loadingStudents}
                    value={findOption(filteredStudents, selectedStudent)}
                    disabled={
                      submitting ||
                      !selectedGradeClass ||
                      !selectedYearId ||
                      (isTeacher && editMode)
                    }
                    getOptionLabel={getOptionLabel}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    onChange={(_, value) => {
                      setSelectedStudent(value?.id ?? "");
                    }}
                    noOptionsText={
                      !selectedGradeClass
                        ? "Select a grade class first"
                        : loadingStudents
                          ? "Loading students..."
                          : "No students found for this class"
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select The Student"
                        placeholder={
                          loadingStudents
                            ? "Loading registered students..."
                            : "Search and select a student"
                        }
                        required
                        sx={fieldSx}
                        error={Boolean(studentsError)}
                        helperText={studentsError}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingStudents && (
                                <CircularProgress
                                  color="inherit"
                                  size={18}
                                  sx={{ mr: 1 }}
                                />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    fullWidth
                    options={subjects || []}
                    value={findOption(subjects, selectedSubject)}
                    disabled={submitting || (isTeacher && editMode)}
                    getOptionLabel={getOptionLabel}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    onChange={(_, value) => {
                      setSelectedSubject(value?.id ?? "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Choose The Subject"
                        placeholder="Select course"
                        required
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    fullWidth
                    options={periods || []}
                    value={findOption(periods, selectedPeriod)}
                    disabled={submitting || (isTeacher && editMode)}
                    getOptionLabel={getOptionLabel}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    onChange={(_, value) => {
                      setSelectedPeriod(value?.id ?? "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Period"
                        placeholder="Select period"
                        required
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* ASSESSMENT SCORES */}

            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: "13px",
                border: `1px solid ${COLORS.border}`,
                bgcolor: COLORS.white,
                mb: 2,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Box
                  sx={{
                    width: 33,
                    height: 33,
                    borderRadius: "9px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#FFF7DB",
                    color: COLORS.orange,
                  }}
                >
                  <Assessment sx={{ fontSize: 20 }} />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      color: COLORS.navy,
                      fontWeight: 850,
                      fontSize: "13px",
                    }}
                  >
                    Assessment Scores
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.25,
                      color: COLORS.muted,
                      fontSize: "10px",
                    }}
                  >
                    Quiz, Assignment, and Participation default to zero.
                    Test Score is required.
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  bgcolor: "#F8FAFC",
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: "9px",
                  p: 1.25,
                  mb: 2,
                }}
              >
                <InfoOutlined
                  sx={{
                    color: COLORS.deepBlue,
                    fontSize: 18,
                    mt: 0.1,
                    flexShrink: 0,
                  }}
                />

                <Typography
                  sx={{
                    color: COLORS.muted,
                    fontSize: "10px",
                    lineHeight: 1.7,
                  }}
                >
                  Quiz, Assignment, and Participation start at{" "}
                  <strong>0</strong>. If left empty, they are submitted as
                  zero. <strong>Test Score is required.</strong> Scale Grade
                  is optional.
                </Typography>
              </Box>

              <Grid container spacing={1.75}>
                {SCORE_FIELDS.map((field) => (
                  <Grid item xs={12} sm={6} key={field.key}>
                    <TextField
                      fullWidth
                      type="number"
                      label={field.label}
                      value={scores[field.key]}
                      disabled={submitting}
                      placeholder={
                        field.defaultZero
                          ? "Enter score (default 0)"
                          : field.key === "testScore"
                            ? "Enter test score"
                            : "Optional — leave blank"
                      }
                      onChange={(event) =>
                        handleScoreChange(field.key, event.target.value)
                      }
                      required={field.required}
                      sx={fieldSx}
                      inputProps={{
                        min: 0,
                        step: "any",
                        inputMode: "decimal",
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Assessment
                              sx={{
                                color: COLORS.muted,
                                fontSize: 17,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      helperText={
                        field.defaultZero
                          ? `${field.description} • Defaults to 0`
                          : field.key === "testScore"
                            ? `${field.description} • Required`
                            : `${field.description} • Optional`
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* TEACHER PERMISSION STATUS
                This is informational only. Permission is never
                editable from this form. */}

            {editMode && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.75,
                  borderRadius: "13px",
                  border: `1px solid ${COLORS.border}`,
                  bgcolor: COLORS.white,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 37,
                      height: 37,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: selectedGrade?.allow_teacher_update
                        ? "#DCFCE7"
                        : "#F1F5F9",
                      color: selectedGrade?.allow_teacher_update
                        ? COLORS.success
                        : COLORS.muted,
                      flexShrink: 0,
                    }}
                  >
                    {selectedGrade?.allow_teacher_update ? (
                      <LockOpenOutlined />
                    ) : (
                      <LockOutlined />
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: COLORS.navy,
                        fontWeight: 800,
                        fontSize: "12px",
                      }}
                    >
                      Teacher Editing Permission
                    </Typography>

                    <Typography
                      sx={{
                        color: COLORS.muted,
                        fontSize: "10px",
                        mt: 0.35,
                        lineHeight: 1.6,
                      }}
                    >
                      {selectedGrade?.allow_teacher_update
                        ? "This record is currently unlocked. A teacher's successful update consumes the permission."
                        : "This record is locked. An administrator or principal must grant permission before a teacher can update it."}
                    </Typography>
                  </Box>

                  <Chip
                    size="small"
                    label={
                      selectedGrade?.allow_teacher_update
                        ? "Unlocked"
                        : "Locked"
                    }
                    sx={{
                      fontWeight: 750,
                      color: selectedGrade?.allow_teacher_update
                        ? "#166534"
                        : "#475569",
                      bgcolor: selectedGrade?.allow_teacher_update
                        ? "#DCFCE7"
                        : "#E9EEF5",
                    }}
                  />
                </Stack>
              </Paper>
            )}
          </Box>
        </DialogContent>

        {/* FOOTER ACTIONS */}

        <DialogActions
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            px: { xs: 1.75, sm: 2.5 },
            gap: 1,
            borderTop: `1px solid ${COLORS.border}`,
            bgcolor: COLORS.white,
            flexDirection: { xs: "column-reverse", sm: "row" },
            "& > :not(style) ~ :not(style)": {
              ml: { xs: 0, sm: 1 },
            },
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={closeDialog}
            disabled={submitting}
            sx={{
              minHeight: 44,
              borderRadius: "10px",
              px: 2.5,
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 800,
              color: COLORS.navy,
              borderColor: COLORS.border,
              width: { xs: "100%", sm: "auto" },
              "&:hover": {
                borderColor: COLORS.deepBlue,
                bgcolor: "#F8FAFC",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            fullWidth
            type="submit"
            form="grade-distribution-form"
            variant="contained"
            disabled={submitting || !canCreateGrades}
            startIcon={
              submitting ? (
                <CircularProgress size={17} color="inherit" />
              ) : (
                <SaveOutlined />
              )
            }
            sx={{
              minHeight: 44,
              borderRadius: "10px",
              px: 3,
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 850,
              color: COLORS.white,
              bgcolor: COLORS.navy,
              boxShadow: "none",
              width: { xs: "100%", sm: "auto" },
              "&:hover": {
                bgcolor: COLORS.deepBlue,
                boxShadow: "0 5px 15px rgba(6,42,75,0.18)",
              },
              "&.Mui-disabled": {
                bgcolor: "#CBD5E1",
                color: COLORS.white,
              },
            }}
          >
            {submitting
              ? "Saving Grade..."
              : editMode
                ? "Update Grade"
                : "Save Grade"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddAndUpDateGrades;
